import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  useTheme,
  InputAdornment,
  styled,
  Chip,
  Menu,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  AccountBalanceWallet as LedgerIcon,
  MoreVert as MoreVertIcon,
  CardGiftcard as GiftIcon,
  Payments as CashIcon
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import { getCurrentUser } from '../../services/authService';
import { customerService } from '../../services/customerService';
import loyaltySettingsService from '../../services/loyaltySettingsService';
import { customersStyles } from './CustomersStyles';
import CustomerForm from './CustomerForm';
import { useNavigate } from 'react-router-dom';

/**
 * Customers management page component
 * @returns {JSX.Element} Customers page
 */
const Customers = () => {
  const theme = useTheme();
  const styles = customersStyles(theme);
  const user = getCurrentUser();
  const companyId = user?.companyId || 1;
  const navigate = useNavigate();
  
  // State management
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0); // kept for other uses but pagination now uses filtered count
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    contactPerson: '',
    address: '',
    website: '',
    notes: '',
    openingBalance: 0,
    companyId: user?.companyId || 1
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState('name');
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuCustomer, setActionMenuCustomer] = useState(null);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [redeemDialogCustomer, setRedeemDialogCustomer] = useState(null);
  const [redeemPointsInput, setRedeemPointsInput] = useState('');
  const [redeemSubmitting, setRedeemSubmitting] = useState(false);
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const searchFieldOptions = ['name', 'contactPerson', 'email'];
  const getSearchLabel = (field) => {
    if (field === 'name') return 'Name';
    if (field === 'contactPerson') return 'Contact Person';
    if (field === 'email') return 'Email';
    return field;
  };
  const handleToggleSearchField = () => {
    const idx = searchFieldOptions.indexOf(searchField);
    const nextField = searchFieldOptions[(idx + 1) % searchFieldOptions.length];
    setSearchField(nextField);
  };
  
  // Memoize loadCustomers function
  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading customers for company:', companyId);
      
      const response = await customerService.getCustomersByCompany(companyId);
      console.log('Loaded customers:', response);
      
      if (Array.isArray(response)) {
        setCustomers(response);
        setTotalElements(response.length);
      } else {
        console.error('Unexpected response format:', response);
        setCustomers([]);
        setTotalElements(0);
      }
    } catch (error) {
      console.error('Error loading customers:', error.response || error);
      setSnackbar({
        open: true,
        message: 'Failed to load customers',
        severity: 'error'
      });
      setCustomers([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [companyId]);
  
  // Initial load
  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Load loyalty settings when redeem dialog opens
  useEffect(() => {
    if (redeemDialogOpen) {
      loyaltySettingsService.get().then(setLoyaltySettings).catch(() => setLoyaltySettings(null));
    }
  }, [redeemDialogOpen]);
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    // Ensure new page is within bounds after any filtering
    const maxPage = Math.max(0, Math.ceil(filteredCustomers.length / rowsPerPage) - 1);
    setPage(Math.min(newPage, maxPage));
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Dialog handlers
  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setSelectedCustomer(customer);
      setFormData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        contactPerson: customer.contactPerson || '',
        address: customer.address || '',
        website: customer.website || '',
        notes: customer.notes || '',
        openingBalance: customer.openingBalance || 0,
        companyId: customer.companyId || user?.companyId || 1
      });
    } else {
      setSelectedCustomer(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        contactPerson: '',
        address: '',
        website: '',
        notes: '',
        openingBalance: 0,
        companyId: user?.companyId || 1
      });
    }
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCustomer(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Parse the opening balance as a float with 2 decimal places
      const initialBalance = parseFloat(parseFloat(formData.openingBalance || 0).toFixed(2));
      const customerData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        contactPerson: formData.contactPerson,
        address: formData.address,
        website: formData.website,
        notes: formData.notes,
        openingBalance: initialBalance,  // Set opening balance
        creditBalance: initialBalance,  // Set credit balance same as opening balance
        companyId: formData.companyId
      };
      
      console.log('Customer data being sent to API:', customerData);
      
      if (selectedCustomer) {
        const response = await customerService.updateCustomer(selectedCustomer.id, customerData);
        console.log('Update response:', response);
        setSnackbar({
          open: true,
          message: 'Customer updated successfully',
          severity: 'success'
        });
      } else {
        const response = await customerService.createCustomer(customerData);
        console.log('Create response:', response);
        setSnackbar({
          open: true,
          message: 'Customer created successfully',
          severity: 'success'
        });
      }
      
      handleCloseDialog();
      loadCustomers();
    } catch (error) {
      console.error('Error saving customer:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to save customer',
        severity: 'error'
      });
    }
  };
  
  // Delete handlers
  const handleOpenDeleteDialog = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCustomerToDelete(null);
  };
  
  const handleDeleteCustomer = async () => {
    try {
      console.log('Toggling status for customer:', customerToDelete);
      const updatedCustomer = await customerService.deleteCustomer(customerToDelete.id);
      
      const newStatus = updatedCustomer.status;
      const actionText = newStatus === 'ACTIVE' ? 'activated' : 'deactivated';
      
      setSnackbar({
        open: true,
        message: `Customer ${actionText} successfully`,
        severity: 'success'
      });
      handleCloseDeleteDialog();
      loadCustomers(); // Reload the customers list after status change
    } catch (error) {
      console.error('Error toggling customer status:', error.response || error);
      setSnackbar({
        open: true,
        message: `Failed to update customer status: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle View Ledger action
  const handleViewLedger = (customer) => {
    navigate(`/customer-ledger/${customer.id}`);
  };

  const handleActionMenuOpen = (event, customer) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuCustomer(customer);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActionMenuCustomer(null);
  };

  const handleActionEdit = (customer) => {
    handleActionMenuClose();
    handleOpenDialog(customer);
  };

  const handleActionViewLedger = (customer) => {
    handleActionMenuClose();
    handleViewLedger(customer);
  };

  const handleActionDelete = (customer) => {
    handleActionMenuClose();
    handleOpenDeleteDialog(customer);
  };

  const handleRedeemClick = (customer) => {
    handleActionMenuClose();
    setRedeemDialogCustomer(customer);
    setRedeemPointsInput('');
    setRedeemDialogOpen(true);
  };

  const handleRedeemDialogClose = () => {
    if (!redeemSubmitting) {
      setRedeemDialogOpen(false);
      setRedeemDialogCustomer(null);
      setRedeemPointsInput('');
    }
  };

  const handleRedeemSubmit = async (redemptionType) => {
    if (!redeemDialogCustomer?.id) return;
    const points = parseInt(redeemPointsInput, 10) || 0;
    const maxPoints = redeemDialogCustomer.loyaltyPoints ?? 0;
    if (points <= 0) {
      setSnackbar({ open: true, message: 'Enter a valid number of points', severity: 'warning' });
      return;
    }
    if (points > maxPoints) {
      setSnackbar({ open: true, message: `Customer has only ${maxPoints} points`, severity: 'warning' });
      return;
    }
    try {
      setRedeemSubmitting(true);
      await customerService.redeemLoyaltyPoints(redeemDialogCustomer.id, points, redemptionType);
      setSnackbar({
        open: true,
        message: `Redeemed ${points} points as ${redemptionType === 'GIFT' ? 'Gift' : 'Cash'} successfully`,
        severity: 'success'
      });
      handleRedeemDialogClose();
      loadCustomers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to redeem points',
        severity: 'error'
      });
    } finally {
      setRedeemSubmitting(false);
    }
  };

  const filteredCustomers = Array.isArray(customers) ? customers.filter(customer => {
    const searchLower = searchQuery.toLowerCase();
    if (searchField === 'name') {
      return customer.name.toLowerCase().includes(searchLower);
    } else if (searchField === 'contactPerson') {
      return (customer.contactPerson || '').toLowerCase().includes(searchLower);
    } else if (searchField === 'email') {
      return (customer.email || '').toLowerCase().includes(searchLower);
    }
    return true;
  }) : [];

  // Reset table page when search query or field changes to avoid showing empty slices
  useEffect(() => {
    setPage(0);
  }, [searchQuery, searchField]);

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'phone', headerName: 'Phone', flex: 1 },
    { field: 'address', headerName: 'Address', flex: 1 },
    { 
      field: 'type', 
      headerName: 'Type', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={params.value === 'credit' ? 'primary' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'openingBalance',
      headerName: 'Opening Balance',
      width: 150,
      renderCell: (params) => (
        <Typography>
          ${params.value.toFixed(2)}
        </Typography>
      )
    },
    { field: 'loyaltyPoints', headerName: 'Points', width: 90 },
    {
      field: 'creditBalance',
      headerName: 'Credit Balance',
      width: 150,
      renderCell: (params) => (
        <Typography color={params.value > 0 ? 'error' : 'success'}>
          ${params.value.toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box sx={styles.actionButtons}>
          <Tooltip title="Actions">
            <IconButton
              size="small"
              onClick={(e) => handleActionMenuOpen(e, params.row)}
              aria-label="Actions"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // Handle customer form success
  const handleCustomerFormSuccess = (customer, error) => {
    if (error) {
      setSnackbar({
        open: true,
        message: error,
        severity: 'error'
      });
    } else {
      setSnackbar({
        open: true,
        message: selectedCustomer ? 'Customer updated successfully' : 'Customer created successfully',
        severity: 'success'
      });
      handleCloseDialog();
      loadCustomers();
    }
  };

  return (
    <Layout>
      <Box sx={styles.root}>
        <Box sx={styles.pageHeader}>
          <Typography variant="h1">Customers Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={styles.addButton}
          >
            Add Customer
          </Button>
        </Box>

        <Box sx={styles.searchSection}>
          <TextField
            placeholder={`Search by ${searchField}`}
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={styles.searchField}
          />
          <Button
            variant="outlined"
            sx={styles.searchButton}
            onClick={handleToggleSearchField}
          >
            Search by: {getSearchLabel(searchField)}
          </Button>
        </Box>

        <TableContainer component={Paper} sx={styles.tableContainer}>
          <Table sx={styles.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell width="5%">S.No</TableCell>
                <TableCell width="14%">Name</TableCell>
                <TableCell width="16%">Email</TableCell>
                <TableCell width="10%">Phone</TableCell>
                <TableCell width="8%">GST</TableCell>
                <TableCell width="8%" align="right">Opening Balance</TableCell>
                <TableCell width="8%" align="right">Points</TableCell>
                <TableCell width="14%">Address</TableCell>
                <TableCell width="6%" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((customer, index) => (
                  <TableRow key={customer.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Tooltip title={customer.name} arrow>
                        <span>{customer.name}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={customer.email} arrow>
                        <span>{customer.email}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{customer.phone}</TableCell>
                    <TableCell>
                      <Tooltip title={customer.gst} arrow>
                        <span>{customer.gst || '-'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {parseFloat(customer.openingBalance || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {customer.loyaltyPoints ?? 0}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={customer.address} arrow>
                        <span>{customer.address}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={styles.actionButtons}>
                        <Tooltip title="Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleActionMenuOpen(e, customer)}
                            aria-label="Actions"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredCustomers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={styles.pagination}
          />
        </TableContainer>

        {/* Shared actions menu (Edit, View Ledger, Deactivate, Redeem) */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => handleActionEdit(actionMenuCustomer)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
          <MenuItem onClick={() => handleActionViewLedger(actionMenuCustomer)}>
            <LedgerIcon fontSize="small" sx={{ mr: 1 }} /> View Ledger
          </MenuItem>
          <MenuItem onClick={() => handleActionDelete(actionMenuCustomer)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> {actionMenuCustomer?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          </MenuItem>
          <MenuItem
            onClick={() => handleRedeemClick(actionMenuCustomer)}
            disabled={!(actionMenuCustomer?.loyaltyPoints > 0)}
          >
            <GiftIcon fontSize="small" sx={{ mr: 1 }} /> Redeem points
          </MenuItem>
        </Menu>

        {/* Customer Form Dialog */}
        <Dialog 
          open={dialogOpen} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          sx={styles.dialog}
        >
          <DialogTitle sx={styles.dialogTitle}>
            {selectedCustomer ? 'Edit Customer' : 'Add Customer'}
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <CustomerForm
            onSuccess={handleCustomerFormSuccess}
            onCancel={handleCloseDialog}
            initialData={selectedCustomer}
          />
        </Dialog>

        {/* Redeem loyalty points dialog (Gift / Cash) */}
        <Dialog open={redeemDialogOpen} onClose={handleRedeemDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            Redeem loyalty points
            <IconButton aria-label="close" onClick={handleRedeemDialogClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {redeemDialogCustomer && (
              <Box sx={{ pt: 0.5 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>{redeemDialogCustomer.name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Available points: <strong>{redeemDialogCustomer.loyaltyPoints ?? 0}</strong>
                  {loyaltySettings?.pointValueRupees != null && (
                    <> · 1 point = ₹{Number(loyaltySettings.pointValueRupees)}</>
                  )}
                </Typography>
                <TextField
                  fullWidth
                  label="Points to redeem"
                  type="number"
                  value={redeemPointsInput}
                  onChange={(e) => setRedeemPointsInput(e.target.value)}
                  inputProps={{ min: 1, max: redeemDialogCustomer.loyaltyPoints ?? 0 }}
                  sx={{ mt: 2 }}
                />
                {loyaltySettings?.pointValueRupees != null && redeemPointsInput && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Value: ₹{((parseInt(redeemPointsInput, 10) || 0) * Number(loyaltySettings.pointValueRupees)).toFixed(2)}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleRedeemDialogClose} disabled={redeemSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={redeemSubmitting ? <CircularProgress size={18} /> : <GiftIcon />}
              onClick={() => handleRedeemSubmit('GIFT')}
              disabled={redeemSubmitting || !redeemPointsInput}
            >
              Redeem as Gift
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={redeemSubmitting ? null : <CashIcon />}
              onClick={() => handleRedeemSubmit('CASH')}
              disabled={redeemSubmitting || !redeemPointsInput}
            >
              Redeem as Cash
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={handleCloseDeleteDialog}
          sx={styles.deleteDialog}
        >
          <DialogTitle sx={styles.dialogTitle}>
            {customerToDelete?.status === 'ACTIVE' ? 'Deactivate Customer' : 'Activate Customer'}
          </DialogTitle>
          <DialogContent sx={styles.deleteDialogContent}>
            <Typography>
              {customerToDelete?.status === 'ACTIVE' 
                ? 'Are you sure you want to deactivate this customer? The customer will be hidden from POS and other processes but can be reactivated later.'
                : 'Are you sure you want to activate this customer? The customer will be visible in POS and other processes again.'
              }
            </Typography>
          </DialogContent>
          <DialogActions sx={styles.dialogActions}>
            <Button onClick={handleCloseDeleteDialog} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteCustomer} 
              color={customerToDelete?.status === 'ACTIVE' ? 'warning' : 'success'}
              variant="contained"
            >
              {customerToDelete?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={styles.snackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default Customers; 